import React from "react";

const MAX_GOALS = 20;

export const QUICK_SCORES = [
  [0, 0],
  [1, 0],
  [1, 1],
  [2, 1],
  [2, 2],
];

export function cleanGoals(raw) {
  const digits = String(raw ?? "").replace(/\D/g, "").slice(0, 2);
  if (digits === "") return "";
  return String(Math.min(MAX_GOALS, Number(digits)));
}

function step(value, delta) {
  const n = value === "" || value == null ? (delta > 0 ? 0 : 1) : Number(value);
  return String(Math.max(0, Math.min(MAX_GOALS, n + delta)));
}

function Stepper({ label, onClick, disabled, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="w-12 h-8 rounded-md text-gray-500 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent flex items-center justify-center text-lg font-semibold leading-none select-none"
    >
      {children}
    </button>
  );
}

function GoalBox({ teamName, value, onChange }) {
  return (
    <div className="flex flex-col items-center">
      <Stepper label={`Add a goal for ${teamName}`} onClick={() => onChange(step(value, 1))} disabled={Number(value) >= MAX_GOALS}>
        +
      </Stepper>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        aria-label={`${teamName} goals`}
        value={value ?? ""}
        onChange={(e) => onChange(cleanGoals(e.target.value))}
        onFocus={(e) => e.target.select()}
        placeholder="–"
        className={`score-input w-12 h-12 rounded-lg border text-center text-score font-bold tabular-nums text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 placeholder:text-gray-300 ${
          value === "" || value == null ? "border-gray-300 bg-white" : "border-gray-400 bg-gray-50"
        }`}
      />
      <Stepper
        label={`Remove a goal for ${teamName}`}
        onClick={() => onChange(step(value, -1))}
        disabled={value === "" || value == null ? false : Number(value) <= 0}
      >
        −
      </Stepper>
    </div>
  );
}

/** Two goal boxes with +/− steppers. `value` is { home, away } as strings ("" = not picked). */
export default function ScorePicker({ homeName, awayName, value, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      <GoalBox teamName={homeName} value={value.home} onChange={(home) => onChange({ ...value, home })} />
      <span aria-hidden className="text-gray-300 font-bold text-lg">:</span>
      <GoalBox teamName={awayName} value={value.away} onChange={(away) => onChange({ ...value, away })} />
    </div>
  );
}

export function QuickScoreChips({ value, onPick }) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {QUICK_SCORES.map(([h, a]) => {
        const active = value.home === String(h) && value.away === String(a);
        return (
          <button
            key={`${h}-${a}`}
            type="button"
            aria-pressed={active}
            onClick={() => onPick({ home: String(h), away: String(a) })}
            className={`h-10 flex-1 min-w-0 max-w-[72px] px-1 rounded-full border text-sm font-semibold tabular-nums whitespace-nowrap transition-colors ${
              active
                ? "bg-brand text-white border-brand"
                : "bg-white text-gray-700 border-line hover:border-gray-400"
            }`}
          >
            {h}–{a}
          </button>
        );
      })}
    </div>
  );
}
